import { makeid } from 'utils/helpers/makeid';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createDeviceIssueTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth."
          <br />
          "For quality and compliance purposes, this call may be recorded."
          <br />
          "To protect your privacy, can you please confirm your full name and
          date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingAdherenceBarrierCheck',
      type: 'radio',
      title: <p>Reading Adherence & Barrier Check</p>,
      description: (
        <ScriptWrapper>
          "Before we talk about the device, I’d like to check in on your
          readings. Have you been able to take and send your readings, or has
          this issue been preventing that?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Consistent readings',
          value: 'consistentReadings',
        },
        {
          key: makeid(10),
          label: 'Inconsistent readings',
          value: 'inconsistentReadings',
        },
        {
          key: makeid(10),
          label: 'No readings',
          value: 'noReadings',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  address it."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'readingAdherenceCheckOther',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Barriers identified',
          value: 'barriersIdentified',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  support you."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              name: 'barriersList',
              type: 'checkbox',
              title: <p>Barriers</p>,
              description: '',
              required: false,
              closable: true,
              options: [
                {
                  key: makeid(10),
                  label: 'Device',
                  value: 'device',
                },
                {
                  key: makeid(10),
                  label: 'Supplies',
                  value: 'supplies',
                },
                {
                  key: makeid(10),
                  label: 'Schedule',
                  value: 'schedule',
                },
                {
                  key: makeid(10),
                  label: 'Education',
                  value: 'education',
                },
                {
                  key: makeid(10),
                  label: 'Connectivity',
                  value: 'connectivity',
                },
                {
                  key: makeid(10),
                  label: 'Power/charging',
                  value: 'powerCharging',
                },
                {
                  key: makeid(10),
                  label: 'Other',
                  value: 'other',
                  hideSubitemsIfParentNotSelected: true,
                  subItems: [
                    {
                      key: makeid(10),
                      type: 'textarea',
                      name: 'barriersListOther',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceAccuracyConcernIdentified',
      type: 'checkbox',
      title: <p>Device Issue Identification — Accuracy Concern</p>,
      description: (
        <ScriptWrapper>
          "I understand you're concerned that the readings from your device
          don't seem accurate."
          <br />
          "Can you tell me what you're comparing them to — another home device
          or a reading from the clinic?"
          <br />
          <br />
          (Allow patient to explain.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Compared to another home device',
          value: 'comparedToAnotherHomeDevice',
        },
        {
          key: makeid(10),
          label: 'Compared to clinic/office reading',
          value: 'comparedToClinicOfficeReading',
        },
        {
          key: makeid(10),
          label: 'Patient reports readings appear inaccurate',
          value: 'patientReportsReadingsAppearInaccurate',
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'deviceAccuracyConcernIdentifiedOther',
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'accuracyEducationTechniqueReview',
      type: 'checkbox',
      title: <p>Accuracy Education & Technique Review</p>,
      description: (
        <ScriptWrapper>
          "Thank you for sharing that — it's a common concern."
          <br />
          "Readings can sometimes differ between devices or compared to an
          office visit."
          <br />
          <br />
          "Differences can happen because of things like:"
          <br />
          <ul>
            <li>– Body position or movement</li>
            <li>– Timing of the reading</li>
            <li>– Cuff placement or fit</li>
            <li>– Stress, pain, or recent activity</li>
          </ul>
          <br />
          "These devices are used to monitor trends over time, not to diagnose
          conditions."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Education on factors affecting readings provided',
          value: 'educationOnFactorsAffectingReadingsProvided',
        },
        {
          key: makeid(10),
          label: 'Proper positioning reviewed',
          value: 'properPositioningReviewed',
        },
        {
          key: makeid(10),
          label: 'Correct cuff placement confirmed',
          value: 'correctCuffPlacementConfirmed',
        },
        {
          key: makeid(10),
          label: 'Re-read performed',
          value: 'reReadPerformed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'accuracyOutcomeDetermination',
      type: 'radio',
      title: <p>Accuracy Determination & Replacement Decision</p>,
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Readings consistent after technique review',
          value: 'readingsConsistentAfterTechniqueReview',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — those readings look more consistent."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Readings remain inconsistent after technique ruled out',
          value: 'readingsRemainInconsistentAfterTechniqueRuledOut',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for working through that with me. Since we've
                  confirmed the technique is correct and the readings still
                  don't appear accurate, we'll move forward with a device
                  replacement."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceReplacementDecision',
      type: 'radio',
      title: <p>Device Replacement Decision</p>,
      description: '',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Replacement not required',
          value: 'replacementNotRequired',
        },
        {
          key: makeid(10),
          label: 'Device replacement approved',
          value: 'deviceReplacementApproved',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceReplacementActions',
      type: 'checkbox',
      title: <p>Device Replacement & Next Steps</p>,
      description: (
        <ScriptWrapper>
          "I'll document this and arrange for a replacement device to be sent to
          you."
          <br />
          "We'll also include instructions so you can start using the new device
          as soon as it arrives."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Replacement device ordered',
          value: 'replacementDeviceOrdered',
        },
        {
          key: makeid(10),
          label: 'Shipping instructions reviewed with patient',
          value: 'shippingInstructionsReviewedWithPatient',
        },
        {
          key: makeid(10),
          label: 'Follow-up required',
          value: 'followUpRequired',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "And what's the best cell phone number to reach you? Is [number on
          file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone confirmed',
          value: 'cellPhoneConfirmed',
        },
        {
          key: makeid(10),
          label: 'Updated',
          value: 'updated',
        },
        {
          key: makeid(10),
          label: 'No cell phone',
          value: 'noCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          "As a reminder, these monthly phone calls are required to remain
          active in the RPM program."
          <br />
          "They help us stay connected and make sure your monitoring is working
          as expected."
          <br />
          "Would you like to schedule your next monthly check-in now, or should
          we call you closer to the date?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TDeviceIssueTemplateValues = ExtractStepValues<
  ReturnType<typeof createDeviceIssueTemplate>
>;

export const createDeviceIssueBlurb = (
  data: TDeviceIssueTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    readingAdherenceBarrierCheck,
    barriersList,
    barriersListOther,
    deviceAccuracyConcernIdentified,
    deviceAccuracyConcernIdentifiedOther,
    accuracyEducationTechniqueReview,
    accuracyOutcomeDetermination,
    deviceReplacementDecision,
    deviceReplacementActions,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    closingAdditionalQuestions,
    closingUnderstanding,
    readingAdherenceCheckOther,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    readingAdherenceBarrierCheck &&
    !hasField(ctx, 'readingAdherenceBarrierCheck')
  ) {
    addField(ctx, 'readingAdherenceBarrierCheck');
    const map = {
      consistentReadings: 'consistent readings',
      inconsistentReadings: 'inconsistent readings',
      missedReadings: 'missed readings',
      noReadings: 'no readings',
      barriersIdentified: 'barriers identified',
      other: readingAdherenceCheckOther,
    };

    const value = map[readingAdherenceBarrierCheck];

    if (value) {
      txt.push(`Reading adherence reviewed. Patient reports ${value}.`);
    }

    if (
      readingAdherenceBarrierCheck === 'barriersIdentified' &&
      barriersList?.length &&
      !hasField(ctx, 'barriersList')
    ) {
      addField(ctx, 'barriersList');
      const barriersMap = {
        device: 'device issues',
        supplies: 'supply availability',
        schedule: 'schedule conflicts',
        education: 'education needs',
        connectivity: 'connectivity issues',
        powerCharging: 'power or charging issues',
        other: barriersListOther,
      };
      const items = mapValuesToText(barriersList, barriersMap);
      if (items.length) {
        txt.push(
          `Barriers identified and documented, including ${formatList(items)}.`,
        );
      }
    }
  }

  if (
    deviceAccuracyConcernIdentified?.length &&
    !hasField(ctx, 'deviceAccuracyConcernIdentified')
  ) {
    addField(ctx, 'deviceAccuracyConcernIdentified');
    const map = {
      other: deviceAccuracyConcernIdentifiedOther,
      comparedToAnotherHomeDevice: 'compared to another home device',
      comparedToClinicOfficeReading: 'compared to clinic office reading',
      patientReportsReadingsAppearInaccurate:
        'patient reports readings appear inaccurate',
    };
    const items = mapValuesToText(deviceAccuracyConcernIdentified, map);

    if (items.length) {
      txt.push(
        `Patient reported concern regarding device accuracy, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    accuracyEducationTechniqueReview?.length &&
    !hasField(ctx, 'accuracyEducationTechniqueReview')
  ) {
    addField(ctx, 'accuracyEducationTechniqueReview');
    const map = {
      educationOnFactorsAffectingReadingsProvided:
        'education on factors affecting readings provided',
      properPositioningReviewed: 'proper positioning reviewed',
      correctCuffPlacementConfirmed: 'correct cuff placement confirmed',
      reReadPerformed: 're-read performed',
    };
    const items = mapValuesToText(accuracyEducationTechniqueReview, map);

    if (items.length) {
      txt.push(
        `Education provided regarding factors affecting readings, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    accuracyOutcomeDetermination &&
    !hasField(ctx, 'accuracyOutcomeDetermination')
  ) {
    addField(ctx, 'accuracyOutcomeDetermination');
    const map = {
      readingsConsistentAfterTechniqueReview:
        'readings consistent after technique review',
      readingsRemainInconsistentAfterTechniqueRuledOut:
        'readings remain inconsistent after technique ruled out',
    };
    txt.push(
      `Accuracy outcome documented as ${map[accuracyOutcomeDetermination]}.`,
    );
  }

  if (
    deviceReplacementDecision &&
    !hasField(ctx, 'deviceReplacementDecision')
  ) {
    addField(ctx, 'deviceReplacementDecision');
    const map = {
      replacementNotRequired: 'not required',
      deviceReplacementApproved: 'approved',
    };
    txt.push(
      `Device replacement ${map[deviceReplacementDecision]} based on persistent inaccuracy after technique review.`,
    );
  }

  if (
    deviceReplacementActions?.length &&
    !hasField(ctx, 'deviceReplacementActions')
  ) {
    addField(ctx, 'deviceReplacementActions');
    const map = {
      replacementDeviceOrdered: 'replacement device ordered',
      shippingInstructionsReviewedWithPatient:
        'shipping instructions reviewed with patient',
      followUpRequired: 'follow-up required',
    };
    const items = mapValuesToText(deviceReplacementActions, map);

    if (items.length) {
      txt.push(`Replacement actions taken: ${formatList(items)}.`);
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]}.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneConfirmed: 'confirmed',
      updated: 'updated',
      noCellPhone: 'no cell phone',
    };
    txt.push(
      `Cell phone contact reviewed: number ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted enrollment',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible, no cell phone',
    };
    txt.push(`Texting enrollment status: ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone calls required',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'nurse or clinic outreach for out-of-range readings explained',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);

    if (items.length) {
      txt.push(`Patient reminded that ${formatList(items)}.`);
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'next check-in scheduled',
      patientDeclinedScheduling: 'patient declined scheduling',
    };
    txt.push(`${map[monthlyInteractionScheduling]}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
